import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from "@tanstack/react-query";
import { searchAccommodations } from "@/features/alojamientos/api/accommodation.api";
import { toast } from "@/hooks/use-toast";
import { Services } from '@/features/alojamientos/types/accomodation.types';
import { SampleType } from '@/features/alojamientos/services/sampleTypeService';
import eventBus from '@/utils/eventBus';
export interface AssignFilters {
  tipoMuestra: string;
  region: string;
  localidad: string;
  segmento: string;
  estado: string;
  sampletype: string;
}

export interface Establishment {
  id: number;
  establishmentName: string;
  classId: number;
  localityId: number;
  region: string;
  phone: string;
  whatsapp: string;
  observations: string;
  statusId: number;
  capacity: number;
  category: string;
  sampleTypeId: number;
}

interface UseAssignFiltersProps {
  regions: Array<{ id: number; name: string }>;
  localities: Array<{ id: number; name: string }>;
  segments: Array<{ id: string; name: string }>;
  statusOptions: Array<{ id: number; name: string }>;
  sampleTypes: SampleType[];
}

const defaultServices: Services = {
  spa: false,
  gym: false,
  wifi: false,
  breakfast: false,
  casino: false,
  petsAllowed: false,
  cafeteriaBar: false,
  restaurant: false,
  eventRoom: false
};

const initialSearchParams = {
  query: '',
  type: [] as string[],
  category: [] as string[],
  status: [] as string[],
  region: [] as string[],
  locality: [] as string[],
  segment: [] as string[],
  sampleTypes: [] as string[],
  services: defaultServices,
  accessibility: 'No',
  hasEventRoom: false
};

const initialFilters: AssignFilters = {
  tipoMuestra: '',
  region: '',
  localidad: '',
  segmento: '',
  estado: '',
  sampletype: ''
};

export const useAssignFilters = ({
  regions,
  localities,
  segments,
  statusOptions,
  sampleTypes
}: UseAssignFiltersProps) => {
  const filtersRef = useRef<AssignFilters>(initialFilters);
  const activeFiltersRef = useRef<string[]>([]);
  const [searchParams, setSearchParams] = useState(initialSearchParams);
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    const handler = (update: any) => {
       refetch();
    };
    eventBus.on('update', handler);
    return () => {
      eventBus.off('update', handler);
    };
  }, []);

  // Efecto para verificar si debemos hacer la llamada a la API
  useEffect(() => {
    const hasRequiredData =
      regions.length > 0 &&
      localities.length > 0 &&
      segments.length > 0 &&
      statusOptions.length > 0 &&
      sampleTypes.length > 0;

    setShouldFetch(hasRequiredData);
  }, [regions, localities, segments, statusOptions, sampleTypes]);

  const { data: results = [], isLoading: loading, refetch } = useQuery({
    queryKey: ['accommodationSearch', searchParams],
    queryFn: async () => {
      if (!shouldFetch) {
        console.log('⏳ Esperando datos necesarios antes de hacer la búsqueda');
        return [];
      }

      try {
        const response = await searchAccommodations(searchParams);
        eventBus.emit('newResults', response.data);
        return response.data;
      } catch (error: any) {
        console.error("Error en la búsqueda:", error);
        toast({
          title: "Error al buscar",
          description: error?.response?.data?.message || "Ocurrió un error en la búsqueda.",
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: shouldFetch,
    staleTime: 0, // Forzar revalidación inmediata
    gcTime: 0     // No mantener en caché
  });

  const updateSearchParams = useCallback((newFilters: AssignFilters) => {
    const newSearchParams = {
      ...initialSearchParams,
      type: newFilters.tipoMuestra ? [newFilters.tipoMuestra] : [],
      status: newFilters.estado ? [newFilters.estado] : [],
      region: newFilters.region ? [newFilters.region] : [],
      locality: newFilters.localidad ? [newFilters.localidad] : [],
      segment: newFilters.segmento ? [newFilters.segmento] : [],
      sampleTypes: newFilters.sampletype ? [newFilters.sampletype] : [],
    };

    if (JSON.stringify(newSearchParams) !== JSON.stringify(searchParams)) {
      console.log('🔄 Actualizando parámetros de búsqueda:', newSearchParams);
      setSearchParams(newSearchParams);
      //setShouldFetch(true);
    }
  }, [searchParams]);

  const handleFilterChange = useCallback((key: keyof AssignFilters, value: string) => {
    filtersRef.current = {
      ...filtersRef.current,
      [key]: value
    };

    // Actualizar filtros activos
    const newActiveFilters = Object.entries(filtersRef.current)
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => {
        const label = key.charAt(0).toUpperCase() + key.slice(1);
        return `${label}: ${value}`;
      });

    activeFiltersRef.current = newActiveFilters;
    
    // Actualizar parámetros de búsqueda
    updateSearchParams(filtersRef.current);
  }, [updateSearchParams]);

  const removeFilter = useCallback((key: keyof AssignFilters) => {
    filtersRef.current = {
      ...filtersRef.current,
      [key]: ''
    };

    // Actualizar filtros activos
    const newActiveFilters = Object.entries(filtersRef.current)
      .filter(([_, value]) => value !== '')
      .map(([key, value]) => {
        const label = key.charAt(0).toUpperCase() + key.slice(1);
        return `${label}: ${value}`;
      });

    activeFiltersRef.current = newActiveFilters;
    
    // Actualizar parámetros de búsqueda
    updateSearchParams(filtersRef.current);
  }, [updateSearchParams]);

  const clearFilters = useCallback(() => {
    filtersRef.current = initialFilters;
    activeFiltersRef.current = [];
    updateSearchParams(initialFilters);
  }, [updateSearchParams]);

  return {
    filters: filtersRef.current,
    activeFilters: activeFiltersRef.current,
    results,
    loading,
    refetch,
    handleFilterChange,
    removeFilter,
    clearFilters
  };
}; 